import { z } from 'zod';
import { auditSchema, idSchema, isoDateTimeSchema } from '../../common.js';
/**
 * Trackers (Dan, 2026-08-18 — "a Sanders tracker", Sanders being the dog).
 *
 * **A tracker is not a chore list, and this distinction is the whole design.** The question
 * a household actually asks is *"has anyone fed the dog?"* — not *"whose turn is it?"* The
 * real failure modes are double-feeding him and nobody walking him, and both are **missing
 * information**, not missing motivation. So a tracker is an information radiator: it never
 * asks anything of anyone, it answers a question you already had.
 *
 * That is also what keeps it on the right side of the no-demand-mechanics rule the games
 * and check-in modules are held to. Concretely, and permanently:
 *
 *  - **No counts.** Not "fed 12 times this week", not a per-member total, not a household
 *    total. A tap is a fact about a moment, never a unit of score. Same rule as
 *    `apps/web/src/modules/games/archive.ts` and `checkin/feedItems.ts`.
 *  - **No streaks, and no "due".** There is no such thing as an overdue tracker. A tracker
 *    that has not been tapped in a week is not failing at anything — its card simply says
 *    so, quietly, and that is the entire feature.
 *  - **No assignment.** Nobody is ever "on dog duty" in here. Anyone taps, everyone sees.
 *    An assignable, completable thing is a list item, and `lists` already does that well.
 *  - **Nothing turns red.** Freshness *fades* (see `freshnessOf`); it never alarms. If a
 *    future change would let the UI scold somebody, it is the wrong change.
 *
 * `cadence` exists **only** to set how fast that fade runs, so "fed" (hours) and "bath"
 * (weeks) don't look identical an hour after each was tapped. It is never a deadline and
 * nothing in the app may notify on it.
 *
 * Shape follows `lists` exactly — a registry doc naming the trackers, plus one doc per
 * tracker holding its taps — because the access story (per-doc audience, WS-15) and the
 * "don't load every tap of every tracker to render an index" story both already work that
 * way.
 */
/** Registry doc: `Y.Map<trackerId, TrackerMeta>`. */
export const TRACKERS_DOC = 'trackers';
export const TRACKERS_MAP_KEY = 'trackers';
/** Per-tracker tap log doc name prefix — matches `syncDocNameSchema`'s `name:segment`. */
export const TRACKER_DOC_PREFIX = 'tracker:';
export const TRACKER_TAPS_KEY = 'taps';
export function trackerDocName(trackerId) {
    return `${TRACKER_DOC_PREFIX}${trackerId}`;
}
/**
 * How quickly an action's freshness fades, as a rough "how often does this happen".
 * Deliberately four coarse buckets rather than a number of hours the member types: the
 * only thing this drives is a visual fade, and a fake-precise "every 9 hours" would invite
 * reading it as a schedule, which is exactly what this module refuses to be.
 */
export const TRACKER_CADENCES = ['often', 'daily', 'weekly', 'occasional'];
export const trackerCadenceSchema = z.enum(TRACKER_CADENCES);
export const TRACKER_CADENCE_HOURS = {
    often: 8,
    daily: 24,
    weekly: 24 * 7,
    occasional: 24 * 30,
};
/** Human label for the cadence, for the tracker editor. Never shown as a due date. */
export const TRACKER_CADENCE_LABELS = {
    often: 'A few times a day',
    daily: 'About daily',
    weekly: 'About weekly',
    occasional: 'Now and then',
};
/** Cap on actions per tracker — a row of pills you can hit with a thumb, not a form. */
export const TRACKER_MAX_ACTIONS = 8;
/**
 * How recently someone else must have tapped the same action for the UI to ask "…already
 * did that N minutes ago — log anyway?" before writing a second tap.
 *
 * This is the double-feed guard, and it is a **confirmation, never a block**: two people
 * really can walk the dog an hour apart, and the tracker's job is to inform, not to
 * arbitrate. Thirty minutes is long enough to catch "we both did it in the kitchen just
 * now" and short enough not to nag about a genuine second walk.
 */
export const TRACKER_DOUBLE_TAP_WINDOW_MS = 30 * 60 * 1000;
export const trackerActionSchema = z.object({
    id: idSchema,
    label: z.string().trim().min(1).max(40),
    emoji: z.string().trim().min(1).max(8).optional(),
    cadence: trackerCadenceSchema,
});
export const trackerMetaSchema = auditSchema.extend({
    id: idSchema,
    name: z.string().trim().min(1).max(60),
    emoji: z.string().trim().min(1).max(8).optional(),
    actions: z.array(trackerActionSchema).max(TRACKER_MAX_ACTIONS),
    /**
     * Pinned trackers get a card on the digest. Several may be pinned; the digest renders
     * them in registry order. Sanders is the reason this field exists.
     */
    pinned: z.boolean(),
    archived: z.boolean(),
});
/**
 * One tap: somebody did this thing, at this moment.
 *
 * `at` is separate from the audit `createdAt` a tracker's meta carries because they answer
 * different questions — "when was the dog fed" versus "when was that written down". They
 * differ whenever someone back-fills a tap they forgot, and the strip must plot the former.
 */
export const trackerTapSchema = z.object({
    id: idSchema,
    actionId: idSchema,
    byId: idSchema,
    at: isoDateTimeSchema,
});
/**
 * Freshness of an action, `1` (just now) → `0` (a full cadence ago or longer). Never
 * negative, never above 1, and `0` for "never tapped" so a brand-new tracker renders the
 * same as a long-untouched one — because it *is* the same statement: nobody has done this
 * lately. Views map this onto opacity only; nothing about it may become a colour warning.
 */
export function freshnessOf(lastAt, cadence, now) {
    if (!lastAt)
        return 0;
    const at = Date.parse(lastAt);
    if (Number.isNaN(at))
        return 0;
    const elapsed = now.getTime() - at;
    if (elapsed <= 0)
        return 1; // clock skew, or a tap dated slightly ahead — treat as fresh
    const span = TRACKER_CADENCE_HOURS[cadence] * 60 * 60 * 1000;
    return Math.max(0, Math.min(1, 1 - elapsed / span));
}
/** Newest tap per action id. The card's "last done · who" comes entirely from this. */
export function lastTapPerAction(taps) {
    const latest = new Map();
    for (const tap of taps) {
        const held = latest.get(tap.actionId);
        if (!held || Date.parse(tap.at) > Date.parse(held.at))
            latest.set(tap.actionId, tap);
    }
    return latest;
}
/** Newest first, ties broken by id so nothing shuffles between renders (as `feedItems`). */
export function sortTapsNewestFirst(taps) {
    return [...taps].sort((a, b) => {
        const diff = Date.parse(b.at) - Date.parse(a.at);
        return diff !== 0 ? diff : b.id.localeCompare(a.id);
    });
}
/**
 * The most recent tap of `actionId` inside the double-tap window, or `null`. Drives the
 * "…already did that" confirmation — including when *you* were the one who tapped, since
 * a double-tap on a phone in a pocket is at least as likely as two people overlapping.
 */
export function recentTap(taps, actionId, now, windowMs = TRACKER_DOUBLE_TAP_WINDOW_MS) {
    const cutoff = now.getTime() - windowMs;
    let best = null;
    for (const tap of taps) {
        if (tap.actionId !== actionId)
            continue;
        const at = Date.parse(tap.at);
        if (Number.isNaN(at) || at < cutoff || at > now.getTime())
            continue;
        if (!best || at > Date.parse(best.at))
            best = tap;
    }
    return best;
}
/** Taps that fall on `day` in local time, oldest first — the today strip's input. */
export function tapsOnDay(taps, day) {
    const start = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
    const end = start + 24 * 60 * 60 * 1000;
    return taps
        .filter((tap) => {
        const at = Date.parse(tap.at);
        return !Number.isNaN(at) && at >= start && at < end;
    })
        .sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}
/**
 * Where a tap sits across its local day, `0` (midnight) → `1` (the next midnight). The
 * strip spans the **whole** day rather than waking hours, so a 5am walk plots honestly
 * instead of being clamped onto the left edge alongside breakfast.
 */
export function fractionOfDay(at, day) {
    const start = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
    const parsed = Date.parse(at);
    if (Number.isNaN(parsed))
        return 0;
    return Math.max(0, Math.min(1, (parsed - start) / (24 * 60 * 60 * 1000)));
}
/**
 * Short relative label for a tap: "just now", "20m", "3h", "2d". Terse on purpose — it sits
 * under a pill next to a member's name, and "about 3 hours ago" would wrap on a phone.
 */
export function tapAgoLabel(at, now) {
    if (!at)
        return null;
    const parsed = Date.parse(at);
    if (Number.isNaN(parsed))
        return null;
    const mins = Math.floor((now.getTime() - parsed) / 60_000);
    if (mins < 1)
        return 'just now';
    if (mins < 60)
        return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24)
        return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7)
        return `${days}d`;
    const weeks = Math.floor(days / 7);
    return weeks < 5 ? `${weeks}w` : 'a while';
}
/**
 * A tracker's planned schedule (2026-09-25, dog-log day view) — e.g. "Walk · 7:00", "Fed ·
 * 18:00". Optional and per-tracker: most trackers (plants, bins) have no schedule at all,
 * so this lives in the tracker's own `tracker:<id>` doc as a small sibling map to `taps`
 * (`PLANNED_ITEMS_KEY`), never on `TrackerMeta` — a tracker with an empty plan should cost
 * nothing to render.
 *
 * This is deliberately **not** a due-date system: nothing here computes "late", nothing
 * turns red, and a day with an unmatched planned item looks exactly as calm as a day with
 * none. It only answers "what was the plan" next to "what actually happened", for the one
 * page (the day view) that wants both at once — the tap log and its freshness fade
 * everywhere else stay entirely unaware this exists.
 */
export const PLANNED_ITEMS_KEY = 'planned';
/** Cap mirrors `TRACKER_MAX_ACTIONS` — a handful of rows on a day view, not a form. */
export const TRACKER_MAX_PLANNED_ITEMS = 8;
export const plannedItemSchema = auditSchema.extend({
    id: idSchema,
    label: z.string().trim().min(1).max(40),
    /** 24-hour `HH:MM`, household-local — the same wall-clock the day view itself renders. */
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM'),
    /**
     * Which tracker action this plans for, when it plans for one in particular — an id from
     * the same `TrackerMeta.actions`. Optional: "Vet visit" at a fixed time doesn't need to
     * match any button, but "Walk · 7:00" matching only `Walk` taps (not `Fed`) is what makes
     * the day view's pairing mean something instead of "any tap within an hour counts".
     */
    kind: idSchema.optional(),
});
/** The planned items for a local calendar day, earliest first. */
export function plannedForDay(items) {
    return [...items].sort((a, b) => a.time.localeCompare(b.time) || a.id.localeCompare(b.id));
}
/** The instant `item.time` falls on, on the local calendar day `day` falls on. */
export function plannedInstantOn(item, day) {
    const [hourStr, minuteStr] = item.time.split(':');
    return new Date(day.getFullYear(), day.getMonth(), day.getDate(), Number(hourStr ?? 0), Number(minuteStr ?? 0));
}
/**
 * How close a log has to land to its planned time to read as "that's the one" — wide enough
 * that a walk nudged by a school run still pairs up, narrow enough that the *next* day's
 * planned walk doesn't grab a stray evening tap.
 */
export const PLANNED_MATCH_WINDOW_MS = 90 * 60 * 1000;
/**
 * Pairs each planned item with the closest same-day log that could be "that" — same `kind`
 * when the plan names one, within `windowMs` of the planned time, and never the same log
 * twice (a 7:05 tap can't simultaneously satisfy a 7:00 *and* an 8:00 planned walk). Earlier
 * planned items get first claim on a tie, which is the only tie-break that matters here:
 * ordering never changes which pairs are *possible*, only which one wins a shared log.
 */
export function matchPlannedToTaps(planned, taps, day, windowMs = PLANNED_MATCH_WINDOW_MS) {
    const dayTaps = tapsOnDay(taps, day);
    const claimed = new Set();
    return plannedForDay(planned).map((item) => {
        const targetMs = plannedInstantOn(item, day).getTime();
        let best = null;
        let bestDelta = Infinity;
        for (const tap of dayTaps) {
            if (claimed.has(tap.id))
                continue;
            if (item.kind && tap.actionId !== item.kind)
                continue;
            const delta = Math.abs(Date.parse(tap.at) - targetMs);
            if (delta <= windowMs && delta < bestDelta) {
                best = tap;
                bestDelta = delta;
            }
        }
        if (best)
            claimed.add(best.id);
        return { item, tap: best };
    });
}
export const TRACKER_TEMPLATES = [
    {
        name: 'The dog',
        emoji: '🐕',
        actions: [
            { label: 'Walk', emoji: '🦮', cadence: 'often' },
            { label: 'Fed', emoji: '🍖', cadence: 'often' },
            { label: 'Water', emoji: '💧', cadence: 'daily' },
            { label: 'Meds', emoji: '💊', cadence: 'daily' },
        ],
    },
    {
        name: 'Plants',
        emoji: '🪴',
        actions: [{ label: 'Watered', emoji: '💧', cadence: 'weekly' }],
    },
    {
        name: 'Laundry',
        emoji: '🧺',
        actions: [
            { label: 'Washed', emoji: '🫧', cadence: 'weekly' },
            { label: 'Dried', emoji: '🌀', cadence: 'weekly' },
            { label: 'Folded', emoji: '🧦', cadence: 'weekly' },
        ],
    },
    {
        name: 'Bins',
        emoji: '🗑️',
        actions: [
            { label: 'Bins out', emoji: '♻️', cadence: 'weekly' },
            { label: 'Bins in', emoji: '🏡', cadence: 'weekly' },
        ],
    },
];
//# sourceMappingURL=index.js.map